import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUp, ArrowUpRight, Check, Copy, Delete, Keyboard, Music, RotateCcw, Smile, X as CloseIcon } from "lucide-react";
import duckAvatar from "@/assets/Logos/duck.webp";
import { allProjects, resolveThumbnail, LazyVideo, type Project } from "@/components/Projects";
import { experiences } from "@/components/Experience";
import { certifications, awards } from "@/components/Certifications";
import { apps } from "@/components/MobileApps";
import { skillCategories } from "@/components/Skills";
import { CHAT_SOCIALS } from "@/components/SocialLinks";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";
import { fetchPosts, getImageUrl, formatDate } from "@/lib/sanityClient";

type Attachment = { url: string; kind: "image" | "gif" | "sticker"; label?: string };
type Cards = { type: "projects" | "blogs" | "cal" | "song" | "page"; ids: string[] };

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  isStreaming?: boolean;
  pending?: "projects" | "blogs" | "text"; // waiting on the AI; what skeleton to show
  attachment?: Attachment;
  cards?: Cards[];
  intro?: boolean; // the canned hello: shown, never sent to the model
  suggestions?: string[]; // follow-up bubbles under the latest reply
}

// Posts as returned by fetchPosts (Sanity)
type Post = {
  _id: string;
  title: string;
  slug?: { current: string };
  publishedAt?: string;
  mainImage?: unknown;
  body?: { _type: string; children?: { text?: string }[] }[];
};

export const CAL_COM_LINK = "https://cal.com/chirag-gadhvi-wunptv/chirag-gadhvi-ai-consultant";
const GIPHY_KEY = import.meta.env.VITE_GIPHY_API_KEY;

// Pages the twin can open for the visitor with [OPEN /path]
const PAGES: Record<string, { title: string; desc: string; game?: boolean }> = {
  "/skills": { title: "Play the Skills game", desc: "Steer a wall of logos, find twins and snap them away.", game: true },
  "/about": { title: "About me", desc: "Who I am, what I do and how to reach me." },
  "/experience": { title: "My experience", desc: "ShipConsole and freelance work, role by role." },
  "/projects": { title: "All projects", desc: "Everything I've built, with live links." },
  "/blogs": { title: "The blog", desc: "What I write about DevOps, AI and building things." },
  "/certifications": { title: "Certifications", desc: "Courses and awards." },
};
// Follow-ups when the model doesn't suggest any (never repeats what's already been asked)
const FOLLOW_UPS = [
  "What's your favourite project?", "How do you use AI at work?", "What did you cut AWS costs with?",
  "Which blog should I read first?", "What's Mello?", "Can I play a game here?", "What are you learning now?",
  "Why DevOps?", "What's your tech stack?", "Do you freelance?", "What's Vexel?", "How did you win Performer of the Year?",
];
const SONG = { id: "QpJD4K_PLSI", title: "About You", artist: "The 1975" };
const INTRO: ChatMessage = {
  role: "assistant",
  content: "Hey, I'm Chirag 👋 I love building stuff: apps, AI tools and little automations that save people time. Ask me anything!",
  attachment: { url: "https://media.giphy.com/media/mHjjxCf4MOqsw/giphy.gif", kind: "gif", label: "hello" },
  intro: true,
  isStreaming: true,
};
// Intro bubbles; icon paths share one 24×24 stroked <svg> wrapper
const SUGGESTIONS = [
  {
    label: "What are you building?",
    icon: (
      <>
        <path d="M13 11L18 6" />
        <path d="M19 7L17 5L19.5 3.5L20.5 4.5L19 7Z" />
        <path d="M4.02513 8.97487C3.01416 7.96391 2.75095 6.48836 3.23548 5.23548L4.65748 6.65748H6.65748V4.65748L5.23548 3.23548C6.48836 2.75095 7.96391 3.01416 8.97487 4.02513C9.98621 5.03647 10.2493 6.51274 9.76398 7.76593L16.2341 14.236C17.4873 13.7507 18.9635 14.0138 19.9749 15.0251C20.9858 16.0361 21.2491 17.5116 20.7645 18.7645L19.3425 17.3425L17.3425 17.3425V19.3425L18.7645 20.7645C17.5116 21.2491 16.0361 20.9858 15.0251 19.9749C14.0145 18.9643 13.7511 17.4895 14.2349 16.2369L7.76312 9.76507C6.51053 10.2489 5.03571 9.98546 4.02513 8.97487Z" />
        <path d="M12.203 14.5L6.59897 20.1041C6.07115 20.6319 5.2154 20.6319 4.68758 20.1041L3.89586 19.3124C3.36805 18.7846 3.36805 17.9288 3.89586 17.401L9.49994 11.7969" />
      </>
    ),
  },
  {
    label: "Show me your projects",
    icon: (
      <>
        <path d="M7 11.2947C12.284 1.44656 18.8635 1.333 21.4928 2.50724C22.667 5.1365 22.5534 11.716 12.7053 17C12.6031 16.4129 12.0352 14.8749 10.5801 13.4199C9.12512 11.9648 7.58712 11.3969 7 11.2947Z" />
        <path d="M14 16.8C16.0428 17.7334 16.2609 19.4069 16.5439 21C16.5439 21 20.8223 18.0481 18.0856 14" />
        <path d="M7.19998 9.99987C6.26664 7.95709 4.59305 7.73899 3 7.45601C3 7.45601 5.95194 3.17753 10 5.91431" />
        <path d="M6.20866 13.9998C5.57677 14.6317 4.50255 16.4642 5.26082 18.739C7.53564 19.4973 9.36813 18.4231 10 17.7912" />
        <path d="M18.0952 7.753C18.0952 6.7328 17.2682 5.90578 16.248 5.90578C15.2278 5.90578 14.4008 6.7328 14.4008 7.753C14.4008 8.77319 15.2278 9.60022 16.248 9.60022C17.2682 9.60022 18.0952 8.77319 18.0952 7.753Z" />
      </>
    ),
  },
  {
    label: "Latest blogs",
    icon: (
      <>
        <path d="M5.07579 17C4.08939 4.54502 12.9123 1.0121 19.9734 2.22417C20.2585 6.35185 18.2389 7.89748 14.3926 8.61125C15.1353 9.38731 16.4477 10.3639 16.3061 11.5847C16.2054 12.4534 15.6154 12.8797 14.4355 13.7322C11.8497 15.6004 8.85421 16.7785 5.07579 17Z" />
        <path d="M4 22C4 15.5 7.84848 12.1818 10.5 10" />
      </>
    ),
  },
  {
    label: "Favourite song?",
    icon: (
      <>
        <circle cx="18" cy="16" r="3" />
        <circle cx="6" cy="18" r="3" />
        <path d="M9 18V9.92707C9 7.74065 9 6.64744 9.6 5.84015C10.2 5.03285 11.24 4.72675 13.32 4.11456C16.84 3.07853 18.6 2.56052 19.8 3.46872C21 4.37693 21 6.22697 21 9.92707V16" />
      </>
    ),
  },
  {
    label: "Book a call",
    icon: (
      <>
        <path d="M16 2V6M8 2V6" />
        <path d="M13 4H11C7.22876 4 5.34315 4 4.17157 5.17157C3 6.34315 3 8.22876 3 12V14C3 17.7712 3 19.6569 4.17157 20.8284C5.34315 22 7.22876 22 11 22H13C16.7712 22 18.6569 22 19.8284 20.8284C21 19.6569 21 17.7712 21 14V12C21 8.22876 21 6.34315 19.8284 5.17157C18.6569 4 16.7712 4 13 4Z" />
        <path d="M3 10H21" />
        <path d="M9 16.5C9 16.5 10.5 17 11 18.5C11 18.5 13.1765 14.5 16 13.5" />
      </>
    ),
  },
];
const MAX_CHARS = 500; // keeps every request small and cheap

const EMOJI = Array.from(
  "😀😂🤣😊😍🥰😎🤩🥳😇🙂🙃😉😋😜🤪🤔🤨🙄😴😭😱🤯😅😬🥺😡🤗🤝👍👎👏🙌🙏💪👋✌️🤞👀🔥✨💯🎉🎊❤️🧡💛💚💙💜🖤💔🚀💻⌨️🖥️📱☁️🐳⚙️🛠️🤖🧠📚✍️☕🍕🍔🎮🎧🏏⚽🌍🌙⭐🌈🐶🐱🦆🐼🙈💩👻💀🤡"
).filter((c) => c !== "️");

// Google's Noto animated emoji: free, no key, work as stickers
const STICKERS: [string, string][] = [
  ["1f602", "😂"], ["1f923", "🤣"], ["1f60d", "😍"], ["1f929", "🤩"], ["1f973", "🥳"], ["1f60e", "😎"],
  ["1f914", "🤔"], ["1f62d", "😭"], ["1f631", "😱"], ["1f92f", "🤯"], ["1f97a", "🥺"], ["1f970", "🥰"],
  ["1f917", "🤗"], ["1f644", "🙄"], ["1f634", "😴"], ["1f92a", "🤪"], ["1f44b", "👋"], ["1f44d", "👍"],
  ["1f44f", "👏"], ["1f64f", "🙏"], ["1f525", "🔥"], ["2764_fe0f", "❤️"], ["1f4af", "💯"], ["1f680", "🚀"],
  ["1f389", "🎉"], ["2728", "✨"], ["1f440", "👀"], ["1f916", "🤖"], ["1f47b", "👻"], ["1f480", "💀"],
  ["1f648", "🙈"], ["1f4a9", "💩"],
];
const stickerUrl = (code: string) => `https://fonts.gstatic.com/s/e/notoemoji/latest/${code}/512.webp`;

const plainText = (post: Post) =>
  (post.body ?? [])
    .filter((b) => b._type === "block")
    .flatMap((b) => b.children?.map((c) => c.text ?? "") ?? [])
    .join(" ")
    .replace(/\s+/g, " ")
    .slice(0, 240);

// Everything the site already shows, handed to the model so it answers from real data
let knowledge: Promise<string> | null = null;
const getKnowledge = () =>
  (knowledge ??= Promise.all([
    fetch("/llms.txt").then((r) => (r.ok ? r.text() : "")).catch(() => ""),
    (fetchPosts() as Promise<Post[]>).catch(() => [] as Post[]),
  ]).then(([llms, posts]) => {
    if (!posts.length) knowledge = null; // Sanity failed: retry next message
    return [
      "## About",
      "Software and AI Engineer in Hyderabad, India. I build websites, mobile apps and AI tools that make everyday work easier. At ShipConsole I manage the AWS cloud that keeps the product fast and reliable.",
      "## Experience",
      ...experiences.map((e) => `- ${e.title} @ ${e.company} (${e.period || "ongoing"}, ${e.location}): ${e.description.join(" ")}`),
      "## Projects (use these exact titles in [SHOW projects: ...])",
      ...allProjects.map((p) => `- ${p.title} (${p.date}): ${p.description} Tech: ${p.tech.join(", ")}. ${p.link ?? ""}`),
      "## Mobile apps",
      ...apps.map((a) => `- ${a.name}: ${a.status}${a.href ? ` ${a.href}` : ""}`),
      "## Skills",
      ...skillCategories.map((c) => `- ${c.title}: ${c.skills.map((s) => s.name).join(", ")}`),
      "## Certifications",
      ...certifications.map((c) => `- ${c.title} (${c.issuer}, ${c.date})`),
      "## Awards",
      ...awards.map((a) => `- ${a.title}: ${a.meta}`),
      "## Links",
      ...CHAT_SOCIALS.map((s) => `- ${s.label}: ${s.href}`),
      `- Book a call: ${CAL_COM_LINK}`,
      "## About (my own words)",
      "I like building things that take a slow, manual job and make it run on its own, and I care about how they look and feel as much as how well they work. By day: DevOps and backend at ShipConsole (AWS, CI/CD, monitoring). On the side: freelance AI and automation, plus my own products.",
      "## Pages on this site (send people there with [OPEN /path])",
      ...Object.entries(PAGES).map(([path, p]) => `- ${path}: ${p.title}. ${p.desc}`),
      "## Fun things on the site",
      "- /skills is a game: a wall of my skill logos drifts the way you move the mouse (or drag it). Pick a logo, then find its twin and they snap away like Thanos, with a gauntlet cursor.",
      "- Any wrong URL shows a 404 game: catch Claude and Codex (on phones they pop up and vanish).",
      "- Keyboard shortcuts: H home, O about, E experience, S skills, P projects, B blogs, A certifications, C book a call, T light/dark theme, Ctrl+K opens the menu.",
      "## Favourites",
      `- Song: ${SONG.title} by ${SONG.artist}`,
      "## Blog posts (use these slugs in [SHOW blogs: ...])",
      ...posts.map((p) => `- ${p.title} [slug: ${p.slug?.current}] (${formatDate(p.publishedAt)}): ${plainText(p)}`),
      "## More",
      llms,
    ].join("\n");
  }));

// ── AI providers ──────────────────────────────────────────────────────────────

const INLINE_MIME = /^data:(image\/(?:png|jpeg|webp));base64,(.+)$/;
const note = (a?: Attachment) => (a ? `[sent a ${a.kind}${a.label ? `: ${a.label}` : ""}]` : "");
const turnText = (m: ChatMessage) => [m.content, note(m.attachment)].filter(Boolean).join(" ");

// The model is called from api/chat.ts, so the API keys stay on the server
async function ask(history: ChatMessage[]) {
  const turns = history.filter((m) => !m.pending && !m.intro).slice(-12).map((m, i, all) => {
    // Only the newest image goes to the model as pixels; older ones stay a note
    const inline = i === all.length - 1 && m.attachment?.url.match(INLINE_MIME);
    return { role: m.role, text: turnText(m), image: inline ? { mime: inline[1], data: inline[2] } : undefined };
  });
  try {
    const res = await fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: AbortSignal.timeout(60000),
      body: JSON.stringify({ facts: await getKnowledge(), turns }),
    });
    if (res.status === 429) return "You’ve sent a lot of messages, give me a few minutes and try again.";
    const data = res.ok ? await res.json() : null;
    if (data?.reply) return data.reply as string;
  } catch { /* network error or timeout */ }
  return "I’m swamped right now — give me a minute and try again.";
}

const SHOW_RE = /\[SHOW (projects|blogs):\s*([^\]]*)\]/gi;
const OPEN_RE = /\[OPEN\s+(\/[a-z-]*\/?)\]/gi;
const SUGGEST_RE = /\[SUGGEST:\s*([^\]]*)\]/i;
function parseReply(reply: string) {
  const cards: Cards[] = [...reply.matchAll(SHOW_RE)].map((m) => ({
    type: m[1].toLowerCase() as Cards["type"],
    ids: m[2].split("|").map((s) => s.trim()).filter(Boolean),
  }));
  if (reply.includes("[BOOK_MEETING]")) cards.push({ type: "cal", ids: [] });
  if (/\[SHOW song\]/i.test(reply)) cards.push({ type: "song", ids: [] });
  for (const m of reply.matchAll(OPEN_RE)) {
    const path = m[1].toLowerCase().replace(/\/$/, "");
    if (PAGES[path] && !cards.some((c) => c.type === "page" && c.ids[0] === path)) cards.push({ type: "page", ids: [path] });
  }
  const suggestions = (reply.match(SUGGEST_RE)?.[1] ?? "")
    .split("|").map((q) => q.trim().replace(/^["']|["']$/g, "")).filter((q) => q && q.length <= 60).slice(0, 3);
  const text = reply.replace(SHOW_RE, "").replace(/\[SHOW song\]/gi, "").replace("[BOOK_MEETING]", "")
    .replace(OPEN_RE, "").replace(SUGGEST_RE, "").trim();
  return { text, cards, suggestions };
}

const guessCards = (q: string): ChatMessage["pending"] =>
  /\bblogs?\b|\bposts?\b|\barticles?\b|you write/i.test(q) ? "blogs"
    : /project|\bbuilt\b|\bbuilding\b|\bapps?\b|portfolio|work on/i.test(q) ? "projects" : "text";

// ── UI pieces ─────────────────────────────────────────────────────────────────

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Types the reply out a couple of characters at a time with a blinking caret
function Typewriter({ text, onComplete }: { text: string; onComplete?: () => void }) {
  const [shown, setShown] = useState(() => (reducedMotion() ? text.length : 0));
  useEffect(() => {
    if (shown >= text.length) { onComplete?.(); return; }
    const t = setTimeout(() => setShown((n) => n + 2), /[.,!?]/.test(text[shown - 1] ?? "") ? 120 : 18);
    return () => clearTimeout(t);
  }, [shown, text, onComplete]);
  return (
    <p className="whitespace-pre-wrap break-words">
      {text.slice(0, shown)}
      <span aria-hidden className="ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[3px] bg-foreground animate-caret" />
    </p>
  );
}

// Plain text with bare URLs turned into links
function Linkified({ text }: { text: string }) {
  return (
    <p className="whitespace-pre-wrap break-words">
      {text.split(/(https?:\/\/[^\s)]+)/g).map((part, i) =>
        /^https?:\/\//.test(part) ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-primary">
            {part.replace(/^https?:\/\/(www\.)?/, "")}
          </a>
        ) : (
          part
        )
      )}
    </p>
  );
}

const Skeletons = ({ kind }: { kind: "projects" | "blogs" }) => (
  <div className="flex gap-3 overflow-hidden">
    {[0, 1].map((i) => (
      <div key={i} className="w-60 shrink-0 rounded-xl border border-border bg-popover p-2">
        <div className="shimmer aspect-video w-full rounded-lg" />
        <div className="shimmer mt-3 h-3.5 w-3/4 rounded" />
        <div className="shimmer mt-2 h-3 w-1/2 rounded" />
        {kind === "projects" && <div className="shimmer mt-2 h-3 w-5/6 rounded" />}
      </div>
    ))}
  </div>
);

function ProjectCard({ project }: { project: Project }) {
  return (
    <a
      href={project.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group w-60 shrink-0 snap-start rounded-xl border border-border bg-popover p-2 transition-colors hover:border-foreground/25"
    >
      <div className="shimmer aspect-video w-full overflow-hidden rounded-lg [transform:translateZ(0)]">
        {project.video ? (
          <LazyVideo src={project.video} />
        ) : (
          <img
            src={resolveThumbnail(project.thumbnail) ?? project.thumbnail ?? resolveThumbnail("chiraggadhvi.webp")}
            alt=""
            loading="lazy"
            className="relative z-[1] h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        )}
      </div>
      <div className="px-1 pb-1 pt-2.5">
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-[13px] font-semibold leading-snug text-foreground line-clamp-1">{project.title.split(" – ")[0]}</h4>
          <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-foreground" />
        </div>
        <p className="mt-1 text-[12px] leading-snug text-muted-foreground line-clamp-2">{project.description}</p>
        <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span className="truncate">{project.tech.slice(0, 2).join(" · ")}</span>
          <span className="shrink-0">{project.date}</span>
        </div>
      </div>
    </a>
  );
}

function BlogCard({ post, onOpen }: { post: Post; onOpen: () => void }) {
  const image = getImageUrl(post.mainImage);
  return (
    <Link
      to={`/blogs/${post.slug?.current}`}
      onClick={onOpen}
      className="group w-60 shrink-0 snap-start rounded-xl border border-border bg-popover p-2 transition-colors hover:border-foreground/25"
    >
      <div className="shimmer aspect-video w-full overflow-hidden rounded-lg [transform:translateZ(0)]">
        {image && (
          <img src={image} alt="" loading="lazy" className="relative z-[1] h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
        )}
      </div>
      <div className="px-1 pb-1 pt-2.5">
        <h4 className="text-[13px] font-semibold leading-snug text-foreground line-clamp-2">{post.title}</h4>
        <p className="mt-1.5 text-[11px] text-muted-foreground">{formatDate(post.publishedAt)}</p>
      </div>
    </Link>
  );
}

const rise = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } } as const;

// cal.com booking page embedded right in the chat
function CalEmbed() {
  const [loaded, setLoaded] = useState(false);
  const theme = document.documentElement.classList.contains("dark") ? "dark" : "light";
  return (
    <motion.div {...rise} className="mt-3 overflow-hidden rounded-xl border border-border bg-popover">
      <div className={`h-[400px] ${loaded ? "" : "shimmer"}`}>
        <iframe
          src={`${CAL_COM_LINK}?embed=true&theme=${theme}&layout=month_view`}
          title="Book a call with Chirag"
          onLoad={() => setLoaded(true)}
          className="relative z-[1] h-full w-full"
        />
      </div>
      <a href={CAL_COM_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 border-t border-border py-2 text-[12px] text-muted-foreground hover:text-foreground">
        Open in a new tab <ArrowUpRight className="h-3 w-3" />
      </a>
    </motion.div>
  );
}

function SongCard() {
  return (
    <motion.div {...rise} className="mt-3 max-w-sm rounded-xl border border-border bg-popover p-2">
      <YouTubeEmbed id={SONG.id} className="" />
      <div className="flex items-center gap-2.5 px-1 pt-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent"><Music className="h-4 w-4" /></span>
        <div className="leading-tight">
          <p className="text-[13px] font-semibold text-foreground">{SONG.title}</p>
          <p className="text-[11px] text-muted-foreground">{SONG.artist}</p>
        </div>
      </div>
    </motion.div>
  );
}

function PageCard({ path, onNavigate }: { path: string; onNavigate: () => void }) {
  const p = PAGES[path];
  return (
    <motion.div {...rise} className="mt-3">
      <Link
        to={path}
        onClick={onNavigate}
        className="group flex w-full max-w-sm items-center gap-3 rounded-xl border border-border bg-popover p-3 transition-colors hover:border-foreground/25"
      >
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${p.game ? "bg-amber-500/10" : "bg-accent"}`}>
          {p.game ? <img src="/cursors/thanos-point.png" alt="" width={28} height={28} className="h-7 w-7" /> : <ArrowUpRight className="h-4 w-4 text-muted-foreground" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-semibold text-foreground">{p.title}</span>
          <span className="block truncate text-[12px] text-muted-foreground">{p.desc}</span>
        </span>
        <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
      </Link>
    </motion.div>
  );
}

function CardRow({ cards, posts, onNavigate }: { cards: Cards; posts: Post[]; onNavigate: () => void }) {
  if (cards.type === "page") return <PageCard path={cards.ids[0]} onNavigate={onNavigate} />;
  if (cards.type === "cal") return <CalEmbed />;
  if (cards.type === "song") return <SongCard />;
  const all = cards.ids.some((id) => id.toUpperCase() === "ALL");
  const want = (s = "") => all || cards.ids.some((id) => s.toLowerCase().includes(id.toLowerCase()) || id.toLowerCase().includes(s.toLowerCase().split(" – ")[0]));
  const items =
    cards.type === "projects"
      ? allProjects.filter((p) => want(p.title)).map((p) => <ProjectCard key={p.title} project={p} />)
      : posts.filter((p) => want(p.slug?.current) || want(p.title)).map((p) => <BlogCard key={p._id} post={p} onOpen={onNavigate} />);
  if (!items.length) return null;
  return (
    <motion.div
      {...rise}
      className="-mx-1 mt-3 flex snap-x gap-3 overflow-x-auto px-1 pb-2 custom-scrollbar"
    >
      {items}
    </motion.div>
  );
}

// ── Emoji / sticker / GIF / letters tray (doubles as the phone keyboard) ─────

type Tab = "abc" | "emoji" | "stickers" | "gifs";
const ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];
const NUM_ROWS = ["1234567890", "@#₹&*-+=()", "!?.,'\":/"];

function Tray({
  tab, setTab, showAbc, onInsert, onBackspace, onSend, onSticker,
}: {
  tab: Tab; setTab: (t: Tab) => void; showAbc: boolean;
  onInsert: (s: string) => void; onBackspace: () => void; onSend: () => void; onSticker: (a: Attachment) => void;
}) {
  const [shift, setShift] = useState(false);
  const [nums, setNums] = useState(false);
  const [gifQuery, setGifQuery] = useState("");
  const [gifs, setGifs] = useState<{ id: string; url: string; preview: string; title: string }[] | null>(null);

  useEffect(() => {
    if (tab !== "gifs" || !GIPHY_KEY) return;
    const q = gifQuery.trim();
    const t = setTimeout(() => {
      const url = q
        ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_KEY}&q=${encodeURIComponent(q)}&limit=24&rating=pg`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_KEY}&limit=24&rating=pg`;
      fetch(url)
        .then((r) => r.json())
        .then((d) => setGifs(d.data.map((g: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
          id: g.id, title: g.title, url: g.images.downsized_medium?.url ?? g.images.original.url, preview: g.images.fixed_width_small.url,
        }))))
        .catch(() => setGifs([]));
    }, 300);
    return () => clearTimeout(t);
  }, [tab, gifQuery]);

  const key = "flex h-10 items-center justify-center rounded-md bg-accent text-[15px] text-foreground active:bg-foreground/20 select-none";
  const tabs: [Tab, string][] = [
    ...(showAbc ? [["abc", "ABC"] as [Tab, string]] : []),
    ["emoji", "Emoji"], ["stickers", "Stickers"], ...(GIPHY_KEY ? [["gifs", "GIFs"] as [Tab, string]] : []),
  ];

  return (
    <div className="border-t border-border bg-popover" onMouseDown={(e) => e.preventDefault() /* keep textarea focus on desktop */}>
      <div className="flex gap-1 px-2 pt-2">
        {tabs.map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-3 py-1 text-[12px] font-medium transition-colors ${tab === t ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="h-[232px] overflow-y-auto custom-scrollbar p-2" data-lenis-prevent>
        {tab === "abc" && (
          <div className="flex h-full flex-col justify-between gap-1.5">
            {(nums ? NUM_ROWS : ROWS).map((row, r) => (
              <div key={r} className="flex justify-center gap-1">
                {r === 2 && !nums && (
                  <button aria-label="Shift" onClick={() => setShift((s) => !s)} className={shift ? "flex h-10 w-11 items-center justify-center rounded-md bg-foreground text-background select-none" : `${key} w-11`}>⇧</button>
                )}
                {Array.from(row).map((c) => (
                  <button key={c} onClick={() => { onInsert(shift ? c.toUpperCase() : c); setShift(false); }} className={`${key} flex-1 max-w-[42px]`}>
                    {shift ? c.toUpperCase() : c}
                  </button>
                ))}
                {r === 2 && (
                  <button aria-label="Backspace" onClick={onBackspace} className={`${key} w-11`}><Delete className="h-4 w-4" /></button>
                )}
              </div>
            ))}
            <div className="flex gap-1">
              <button onClick={() => setNums((n) => !n)} className={`${key} w-14 text-[13px]`}>{nums ? "ABC" : "123"}</button>
              <button onClick={() => setTab("emoji")} aria-label="Emoji" className={`${key} w-11`}><Smile className="h-4 w-4" /></button>
              <button onClick={() => onInsert(" ")} className={`${key} flex-1 text-[13px] text-muted-foreground`}>space</button>
              <button onClick={() => onInsert(".")} className={`${key} w-10`}>.</button>
              <button onClick={onSend} aria-label="Send" className="flex h-10 w-14 items-center justify-center rounded-md bg-foreground text-background active:opacity-80 select-none"><ArrowUp className="h-4 w-4" /></button>
            </div>
          </div>
        )}
        {tab === "emoji" && (
          <div className="grid grid-cols-8 gap-1">
            {EMOJI.map((e, i) => (
              <button key={i} onClick={() => onInsert(e)} className="flex h-10 items-center justify-center rounded-md text-[22px] hover:bg-accent active:scale-90 transition-transform">
                {e}
              </button>
            ))}
          </div>
        )}
        {tab === "stickers" && (
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {STICKERS.map(([code, emoji]) => (
              <button key={code} onClick={() => onSticker({ url: stickerUrl(code), kind: "sticker", label: emoji })} className="shimmer aspect-square rounded-lg hover:bg-accent active:scale-90 transition-transform">
                <img src={stickerUrl(code)} alt={emoji} loading="lazy" className="relative z-[1] h-full w-full p-1.5" />
              </button>
            ))}
          </div>
        )}
        {tab === "gifs" && (
          <>
            <input
              value={gifQuery}
              onChange={(e) => setGifQuery(e.target.value)}
              placeholder="Search GIFs"
              className="mb-2 w-full rounded-lg border border-border bg-accent px-3 py-2 text-[16px] text-foreground outline-none placeholder:text-muted-foreground"
            />
            <div className="columns-3 gap-2">
              {gifs === null
                ? [0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="shimmer mb-2 h-20 rounded-lg" />)
                : gifs.map((g) => (
                    <button key={g.id} onClick={() => onSticker({ url: g.url, kind: "gif", label: g.title })} className="shimmer mb-2 block w-full overflow-hidden rounded-lg">
                      <img src={g.preview} alt={g.title} loading="lazy" className="relative z-[1] w-full" />
                    </button>
                  ))}
            </div>
            <p className="pt-1 text-center text-[10px] text-muted-foreground">Powered by GIPHY</p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Chat ──────────────────────────────────────────────────────────────────────

export function Chat({
  messages, setMessages, query, setQuery, onBack, onNavigate, isPhone, autoSend, clearAutoSend,
}: {
  messages: ChatMessage[];
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  query: string;
  setQuery: (v: string) => void;
  onBack: () => void;
  onNavigate: () => void;
  isPhone: boolean;
  autoSend?: string | null;
  clearAutoSend?: () => void;
}) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [draft, setDraft] = useState<Attachment | null>(null);
  const [copied, setCopied] = useState<number | null>(null);
  const [tray, setTray] = useState<Tab | null>(null);
  const [pasteError, setPasteError] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const busy = messages.some((m) => m.pending);

  useEffect(() => {
    (fetchPosts() as Promise<Post[]>).then(setPosts).catch(() => {});
    getKnowledge();
    if (!messages.length) setMessages([{ ...INTRO }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages]);

  const send = async (text = query, attachment = draft) => {
    if ((!text.trim() && !attachment) || busy) return;
    const userMsg: ChatMessage = { role: "user", content: text.trim().slice(0, MAX_CHARS), attachment: attachment ?? undefined };
    const history = [...messages, userMsg];
    setMessages([...history, { role: "assistant", content: "", pending: guessCards(text) }]);
    setQuery("");
    setDraft(null);

    const { text: reply, cards, suggestions } = parseReply(await ask(history));
    // Fresh follow-ups every reply: the model's, topped up from the pool with things not asked yet
    const asked = new Set(history.filter((m) => m.role === "user").map((m) => m.content.toLowerCase()));
    const pool = FOLLOW_UPS.filter((q) => !asked.has(q.toLowerCase()) && !suggestions.includes(q)).sort(() => Math.random() - 0.5);
    const next = [...suggestions.filter((q) => !asked.has(q.toLowerCase())), ...pool].slice(0, 3);
    setMessages((prev) => [...prev.filter((m) => !m.pending), { role: "assistant", content: reply, cards, suggestions: next, isStreaming: true }]);
  };

  useEffect(() => {
    if (autoSend) { send(autoSend); clearAutoSend?.(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSend]);

  // Insert at the caret (desktop) or at the end (custom keyboard, textarea not focused)
  const insert = (s: string) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? query.length;
    const end = el?.selectionEnd ?? query.length;
    setQuery(query.slice(0, start) + s + query.slice(end));
    requestAnimationFrame(() => el?.setSelectionRange(start + s.length, start + s.length));
  };
  const backspace = () => {
    // Drop one whole character, including multi-codepoint emoji
    // Intl.Segmenter is newer than this project's TS lib typings
    const { Segmenter } = Intl as unknown as { Segmenter: new () => { segment(s: string): Iterable<{ segment: string }> } };
    const parts = [...new Segmenter().segment(query)];
    setQuery(parts.slice(0, -1).map((p) => p.segment).join(""));
  };

  const readFile = (file: File) => {
    // Vercel caps request bodies at 4.5 MB and base64 adds a third, so 3 MB is the ceiling
    if (file.size > 3 * 1024 * 1024) { setPasteError("That image is over 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setDraft({ url: String(reader.result), kind: file.type === "image/gif" ? "gif" : "image" });
    reader.readAsDataURL(file);
  };
  const onPaste = (e: React.ClipboardEvent) => {
    setPasteError("");
    const file = Array.from(e.clipboardData.files).find((f) => f.type.startsWith("image/"));
    if (file) { e.preventDefault(); readFile(file); return; }
    // Copying a GIF from a web page often gives HTML with an <img>, not a file
    const src = e.clipboardData.getData("text/html").match(/<img[^>]+src="(https:[^"]+)"/i)?.[1];
    if (src) { e.preventDefault(); setDraft({ url: src, kind: /\.gif(\?|$)/i.test(src) || src.includes("giphy") ? "gif" : "image" }); }
  };

  // Phones: the tray replaces the system keyboard (inputMode none keeps it down)
  const openTray = (t: Tab) => { setTray(t); if (isPhone) inputRef.current?.blur(); };
  const showSystemKeyboard = () => { setTray(null); requestAnimationFrame(() => inputRef.current?.focus()); };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-3 md:px-4">
        <button onClick={onBack} aria-label="Back to menu" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="relative shrink-0">
          <img src="/profile.webp" alt="" width={36} height={36} className="h-9 w-9 rounded-full object-cover" />
          <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-popover bg-emerald-500" />
        </div>
        <h2 className="flex-1 text-[15px] font-semibold text-foreground">My AI Twin</h2>
        <button
          onClick={() => { setMessages([{ ...INTRO }]); setDraft(null); }}
          aria-label="Start over"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>

      <div className="mx-2 mb-2 flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card/60 md:mx-3 md:mb-3">
        {/* Messages */}
        <div
          className={`${tray && isPhone ? "h-[min(260px,32dvh)]" : "h-[min(440px,52dvh)]"} overflow-y-auto custom-scrollbar space-y-5 px-3 py-5 md:px-5`}
          data-lenis-prevent
        >
          {messages.map((msg, idx) =>
            msg.role === "user" ? (
              <div key={idx} className="flex items-end justify-end gap-2">
                <div className="flex max-w-[78%] flex-col items-end gap-1.5">
                  {msg.attachment && (
                    <img
                      src={msg.attachment.url}
                      alt={msg.attachment.label ?? ""}
                      className={msg.attachment.kind === "sticker" ? "h-24 w-24" : "max-h-52 rounded-2xl border border-border"}
                    />
                  )}
                  {msg.content && (
                    <div className="rounded-2xl rounded-br-md bg-accent px-4 py-2.5 text-[15px] leading-relaxed text-foreground">
                      <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                    </div>
                  )}
                </div>
                <img src={duckAvatar} alt="You" width={28} height={28} className="h-7 w-7 shrink-0 rounded-full object-cover" />
              </div>
            ) : (
              <div key={idx} className="flex items-start gap-2.5">
                <img src="/profile.webp" alt="Chirag" width={28} height={28} className="mt-0.5 h-7 w-7 shrink-0 rounded-full object-cover" />
                <div className="min-w-0 flex-1 text-[15px] leading-relaxed text-foreground">
                  {msg.pending ? (
                    <div className="space-y-3 pt-1" aria-label="Chirag is typing">
                      <div className="shimmer h-3.5 w-4/5 rounded" />
                      <div className="shimmer h-3.5 w-3/5 rounded" />
                      {msg.pending !== "text" && <Skeletons kind={msg.pending} />}
                    </div>
                  ) : msg.isStreaming ? (
                    <Typewriter
                      text={msg.content}
                      onComplete={() => setMessages((prev) => prev.map((m, i) => (i === idx ? { ...m, isStreaming: false } : m)))}
                    />
                  ) : (
                    <>
                      <Linkified text={msg.content} />
                      {msg.attachment && (
                        <motion.div {...rise} className="shimmer mt-2.5 w-fit overflow-hidden rounded-2xl border border-border">
                          <img src={msg.attachment.url} alt={msg.attachment.label ?? ""} className="relative z-[1] block max-h-44" />
                        </motion.div>
                      )}
                      {msg.cards?.map((c, i) => <CardRow key={i} cards={c} posts={posts} onNavigate={onNavigate} />)}
                      {msg.intro ? (
                        idx === messages.length - 1 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {SUGGESTIONS.map(({ label, icon }, i) => (
                              <motion.button
                                key={label}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.15 + i * 0.06, duration: 0.3 }}
                                onClick={() => send(label, null)}
                                className="flex items-center gap-2 rounded-2xl rounded-tl-md border border-border bg-accent/50 px-3.5 py-2 text-[13px] text-foreground transition-colors hover:bg-accent"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground">
                                  {icon}
                                </svg>
                                {label}
                              </motion.button>
                            ))}
                          </div>
                        )
                      ) : (
                        <button
                          onClick={() => {
                            navigator.clipboard?.writeText(msg.content).catch(() => {});
                            setCopied(idx);
                            setTimeout(() => setCopied((c) => (c === idx ? null : c)), 1500);
                          }}
                          aria-label="Copy reply"
                          className="mt-1.5 flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        >
                          {copied === idx ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </button>
                      )}
                      {!msg.intro && idx === messages.length - 1 && !!msg.suggestions?.length && (
                        <div className="mt-2 flex flex-wrap gap-2" aria-label="Suggested questions">
                          {msg.suggestions.map((q, i) => (
                            <motion.button
                              key={`${idx}-${q}`}
                              initial={{ opacity: 0, y: 6, scale: 0.96 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              transition={{ delay: 0.1 + i * 0.08, duration: 0.3 }}
                              onClick={() => send(q, null)}
                              className="rounded-2xl rounded-tl-md border border-border bg-accent/50 px-3.5 py-2 text-left text-[13px] text-foreground transition-colors hover:border-foreground/25 hover:bg-accent active:scale-95"
                            >
                              {q}
                            </motion.button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )
          )}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <div className="relative p-2 md:p-3">
          {busy && (
            <>
              <div aria-hidden className="ai-glow pointer-events-none absolute inset-2 rounded-2xl blur-md md:inset-3" />
              <div aria-hidden className="ai-glow pointer-events-none absolute inset-2 -m-px rounded-2xl md:inset-3" />
            </>
          )}
          <div
            className="relative rounded-2xl border border-border bg-popover px-3 pb-2 pt-2.5 transition-colors focus-within:border-foreground/30"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith("image/"));
              if (file) { e.preventDefault(); readFile(file); }
            }}
          >
            {draft && (
              <div className="relative mb-2 inline-block">
                <img src={draft.url} alt="" className="h-20 rounded-lg border border-border object-cover" />
                <button onClick={() => setDraft(null)} aria-label="Remove attachment" className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background">
                  <CloseIcon className="h-3 w-3" />
                </button>
              </div>
            )}
            <textarea
              ref={inputRef}
              autoFocus={!isPhone}
              rows={1}
              value={query}
              inputMode={tray && isPhone ? "none" : "text"}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={(e) => { if (tray && isPhone) setTray(null); e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length); }}
              onPaste={onPaste}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              enterKeyHint="send"
              placeholder="Ask me anything…"
              maxLength={MAX_CHARS}
              className="block max-h-32 w-full resize-none bg-transparent px-1 text-[16px] text-foreground outline-none placeholder:text-muted-foreground"
            />
            <div className="mt-1.5 flex items-center gap-1">
              <button
                onClick={() => (tray ? (isPhone ? showSystemKeyboard() : setTray(null)) : openTray("emoji"))}
                aria-label={tray ? "Close emoji and stickers" : "Emoji, stickers and GIFs"}
                className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-accent ${tray && tray !== "abc" ? "text-foreground" : "text-muted-foreground"}`}
              >
                <Smile className="h-[18px] w-[18px]" />
              </button>
              {isPhone && (
                <button
                  onClick={() => (tray === "abc" ? showSystemKeyboard() : openTray("abc"))}
                  aria-label={tray === "abc" ? "Use phone keyboard" : "Use Chirag's keyboard"}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-accent ${tray === "abc" ? "text-foreground" : "text-muted-foreground"}`}
                >
                  <Keyboard className="h-[18px] w-[18px]" />
                </button>
              )}
              {pasteError && <span className="ml-1 text-[11px] text-destructive">{pasteError}</span>}
              <button
                onClick={() => send()}
                disabled={(!query.trim() && !draft) || busy}
                aria-label="Send message"
                className="ml-auto flex h-9 w-9 items-center justify-center rounded-xl bg-foreground text-background transition-all active:scale-95 disabled:bg-accent disabled:text-muted-foreground"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {tray && (
          <Tray
            tab={tray}
            setTab={setTray}
            showAbc={isPhone}
            onInsert={insert}
            onBackspace={backspace}
            onSend={() => send()}
            onSticker={(a) => { send(query, a); }}
          />
        )}
      </div>
    </div>
  );
}
